// Active-window zoom controls. Steps: 30 / 60 / 90 / 180 days. Endpoints
// disable the matching button. An optional reset button restores the
// smart-default zoom + focus when the user has explicitly overridden them.

import { Minus, Plus, RotateCcw } from "lucide-react";

const ZOOM_STEPS = [30, 60, 90, 180] as const;
export type ZoomStep = (typeof ZOOM_STEPS)[number];

function previousStep(current: ZoomStep): ZoomStep | null {
  const index = ZOOM_STEPS.indexOf(current);
  if (index <= 0) return null;
  return ZOOM_STEPS[index - 1];
}

function nextStep(current: ZoomStep): ZoomStep | null {
  const index = ZOOM_STEPS.indexOf(current);
  if (index < 0 || index === ZOOM_STEPS.length - 1) return null;
  return ZOOM_STEPS[index + 1];
}

export interface TimelineZoomControlsProps {
  value: ZoomStep;
  onChange: (next: ZoomStep) => void;
  /** When provided, renders a reset button. Disabled when canReset is
   *  false so the affordance is visible but not actionable while the
   *  view already matches the smart default. */
  onReset?: () => void;
  canReset?: boolean;
}

export default function TimelineZoomControls({
  value,
  onChange,
  onReset,
  canReset = true,
}: TimelineZoomControlsProps) {
  const prev = previousStep(value);
  const next = nextStep(value);
  const baseButton =
    "inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/40";
  return (
    <div
      data-testid="timeline-zoom-controls"
      className="inline-flex items-center gap-2"
    >
      <button
        type="button"
        data-testid="timeline-zoom-in"
        onClick={() => prev !== null && onChange(prev)}
        disabled={prev === null}
        aria-label={
          prev === null
            ? "Zoom in unavailable"
            : `Zoom in to ${prev}-day window`
        }
        className={baseButton}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <span
        data-testid="timeline-zoom-label"
        className="min-w-[7rem] text-center text-caption text-slate-600 dark:text-slate-300"
      >
        {value}-day window
      </span>
      <button
        type="button"
        data-testid="timeline-zoom-out"
        onClick={() => next !== null && onChange(next)}
        disabled={next === null}
        aria-label={
          next === null
            ? "Zoom out unavailable"
            : `Zoom out to ${next}-day window`
        }
        className={baseButton}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
      {onReset && (
        <button
          type="button"
          data-testid="timeline-zoom-reset"
          onClick={() => canReset && onReset()}
          disabled={!canReset}
          aria-label={
            canReset
              ? "Reset timeline zoom and position to default"
              : "Timeline already at default zoom and position"
          }
          title="Reset to default"
          className={baseButton}
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
